// ./src/components/features/Contacts/ContactsList/ContactsList.tsx
//
// Contacts list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row, and selection-ready
// (checkboxes on desktop/tablet, a selection MODE on phones). Mirrors the
// Memberships list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  Avatar,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import {
  Person,
  getPersonDisplayName,
  getPersonBirthdayLabel,
  isPersonUpcomingBirthday,
} from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalPersonsContext } from "@/contexts/PersonsContext";
import { useOptionalPersonGroupsContext } from "@/contexts/PersonGroupsContext";
import {
  AppIcon,
  TagBadge,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

import { ContactChannels, ContactChannelsMobile } from "../ContactChannels/ContactChannels";

// ============================================
// Props
// ============================================

export interface ContactsListProps {
  data?: Person[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditContact?: (person: Person) => void;
  onDeleteContact?: (person: Person) => void;
  /** Open the contact's notes drawer. Shown for anyone who can view the contact. */
  onOpenNotes?: (person: Person) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddContact?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (person: Person): boolean => person.status === STATUSES.DISABLED;

const DisabledBadge: React.FC = () => {
  const { t } = useTranslation(["common"]);

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const BirthdayBadge: React.FC<{ label: string }> = ({ label }) => (
  <Tooltip label={label} withArrow position="top">
    <Badge size="xs" variant="light" color="pink" leftSection={<AppIcon iconName="Gift" size={11} />}>
      {label}
    </Badge>
  </Tooltip>
);

// The contact's group / household, shown under the name. Resolved from the groups
// context so we can display the name (the person only carries personGroupId).
const ContactGroupLine: React.FC<{ groupName: string | null }> = ({ groupName }) => {
  if (!groupName) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      <AppIcon iconName="UsersGroup" size={12} />
      <Text size="xs" c="dimmed" lineClamp={1}>
        {groupName}
      </Text>
    </Group>
  );
};

const ContactTags: React.FC<{ person: Person; max?: number }> = ({ person, max = 3 }) => {
  const tags = (person.tags ?? []).filter(Boolean);

  if (tags.length === 0) {
    return null;
  }

  const shown = tags.slice(0, max);
  const extra = tags.length - shown.length;

  return (
    <Group gap={4} wrap="nowrap">
      {shown.map((tag) => (
        <TagBadge
          key={tag.id}
          tag={{ id: tag.id, name: tag.name ?? undefined, color: tag.color ?? undefined }}
          size="xs"
        />
      ))}
      {extra > 0 && (
        <Badge size="xs" variant="light" color="gray">
          +{extra}
        </Badge>
      )}
    </Group>
  );
};

const RowActions: React.FC<{
  person: Person;
  onEdit?: (person: Person) => void;
  onDelete?: (person: Person) => void;
  onOpenNotes?: (person: Person) => void;
  isDeleting: boolean;
}> = ({ person, onEdit, onDelete, onOpenNotes, isDeleting }) => {
  const { t } = useTranslation(["contacts", "common"]);

  const showEdit = !!onEdit && recordHasRight(person, "edit");
  const showDelete = !!onDelete && recordHasRight(person, "remove");

  if (!showEdit && !showDelete && !onOpenNotes) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {onOpenNotes && (
        <Tooltip label={t("contacts:notes.action", { defaultValue: "Notes" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onOpenNotes(person)}
            disabled={isDeleting}
            aria-label={t("contacts:notes.action", { defaultValue: "Notes" })}
          >
            <AppIcon iconName="FileText" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(person)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(person)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  person: Person;
  name: string;
  subtitle: string;
  groupName: string | null;
  birthdayLabel: string | null;
  upcomingBirthday: boolean;
  disabled: boolean;
  onEdit?: (person: Person) => void;
  onDelete?: (person: Person) => void;
  onOpenNotes?: (person: Person) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const ContactTableRow: React.FC<RowProps> = ({
  person,
  name,
  subtitle,
  groupName,
  birthdayLabel,
  upcomingBirthday,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  onOpenNotes,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => person.id && onToggleSelect?.(person.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <Avatar name={name} color="initials" radius="xl" size={34} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              {upcomingBirthday && birthdayLabel && <BirthdayBadge label={birthdayLabel} />}
              {disabled && <DisabledBadge />}
            </Group>
            {subtitle && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {subtitle}
              </Text>
            )}
            <ContactGroupLine groupName={groupName} />
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 240 }}>
        <ContactChannels person={person} />
      </Table.Td>
      <Table.Td style={{ width: 200 }}>
        <ContactTags person={person} />
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(person.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <RowActions
          person={person}
          onEdit={onEdit}
          onDelete={onDelete}
          onOpenNotes={onOpenNotes}
          isDeleting={isDeleting}
        />
      </Table.Td>
    </Table.Tr>
  );
};

const ContactTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Box style={{ flex: 1 }}>
          <Skeleton height={14} width="50%" maw={200} mb={6} />
          <Skeleton height={12} width="35%" maw={150} />
        </Box>
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <Skeleton height={14} width={180} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={18} width={120} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={130} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ContactCardComponent: React.FC<CardProps> = ({
  person,
  name,
  subtitle,
  groupName,
  birthdayLabel,
  upcomingBirthday,
  disabled,
  onEdit,
  onDelete,
  onOpenNotes,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["contacts", "common"]);

  const toggleSelect = () => person.id && onToggleSelect?.(person.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onOpenNotes) {
      actions.push({
        key: "notes",
        secondary: true,
        icon: "FileText",
        label: t("contacts:notes.action", { defaultValue: "Notes" }),
        color: "gray",
        onClick: () => onOpenNotes(person),
        disabled: isDeleting,
      });
    }

    if (onEdit && recordHasRight(person, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(person),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(person, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(person),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, onOpenNotes, person, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
            mt={4}
          />
        )}
        <Avatar name={name} color="initials" radius="xl" size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="sm">
            <Stack gap={3}>
              <Group gap={6} wrap="nowrap">
                <Text size="md" fw={600} lineClamp={1}>
                  {name}
                </Text>
                {disabled && <DisabledBadge />}
              </Group>
              {subtitle && (
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {subtitle}
                </Text>
              )}
              <ContactGroupLine groupName={groupName} />
            </Stack>
            <ContactChannelsMobile person={person} />
            {((upcomingBirthday && birthdayLabel) || (person.tags ?? []).length > 0) && (
              <Group gap={6} wrap="wrap">
                {upcomingBirthday && birthdayLabel && <BirthdayBadge label={birthdayLabel} />}
                <ContactTags person={person} max={2} />
              </Group>
            )}
          </Stack>
        </Box>
        {showActions && (
          <RowActions
            person={person}
            onEdit={onEdit}
            onDelete={onDelete}
            onOpenNotes={onOpenNotes}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const ContactCard = React.memo(ContactCardComponent);

// The name and ONE contact row (a 34px Call / Copy line): most contacts have an email and no phone (ROADMAP #127 — it
// drew both, 44px taller than the usual card). A job title, group or tags are rarer and left out.
const ContactCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={2} sizes={["md", "control"]} gap={11} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const ContactsListComponent: React.FC<ContactsListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditContact,
  onDeleteContact,
  onOpenNotes,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddContact,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["contacts", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onOpenNotes, onEditContact, onDeleteContact);

  const personsContext = useOptionalPersonsContext();
  const groupsContext = useOptionalPersonGroupsContext();
  const getGroupName = useCallback(
    (id?: string | null): string | null => (id ? groupsContext?.getById(id)?.name ?? null : null),
    [groupsContext]
  );

  const persons = dataProp ?? personsContext?.persons ?? [];
  const loading = loadingProp ?? personsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? personsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? personsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? personsContext?.loadMore;
  const isRemoving = isRemovingProp ?? personsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: persons.length });

  const handleEdit = useCallback(
    (person: Person) => {
      closeAllCards();
      onEditContact?.(person);
    },
    [onEditContact, closeAllCards]
  );

  const handleDelete = useCallback(
    (person: Person) => {
      closeAllCards();
      onDeleteContact?.(person);
    },
    [onDeleteContact, closeAllCards]
  );

  const handleOpenNotes = useCallback(
    (person: Person) => {
      closeAllCards();
      onOpenNotes?.(person);
    },
    [onOpenNotes, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = personsContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && persons.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("contacts:emptyState.noMatch", { defaultValue: "No contacts match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("contacts:emptyState.title", { defaultValue: "No contacts yet" })}
          description={t("contacts:emptyState.description", {
            defaultValue: "Add a contact to start building your address book.",
          })}
          iconName="AddressBook"
          actionLabel={t("contacts:emptyState.actionLabel", { defaultValue: "Add contact" })}
          onAction={onAddContact}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (person: Person): boolean =>
    isSelectable &&
    ((!!onEditContact && recordHasRight(person, "edit")) ||
      (!!onDeleteContact && recordHasRight(person, "remove")));

  const rowProps = (person: Person): RowProps => {
    const name = getPersonDisplayName(person);
    const subtitle = [person.jobTitle, person.company].map((v) => (v || "").trim()).filter(Boolean).join(" · ");

    return {
      person,
      name,
      subtitle,
      groupName: getGroupName(person.personGroupId),
      birthdayLabel: getPersonBirthdayLabel(person),
      upcomingBirthday: isPersonUpcomingBirthday(person),
      disabled: isDisabled(person),
      onEdit: onEditContact ? handleEdit : undefined,
      onDelete: onDeleteContact ? handleDelete : undefined,
      onOpenNotes: onOpenNotes ? handleOpenNotes : undefined,
      highlighted: person.id === highlightedId,
      isDeleting: isRemoving(person.id),
      language,
      selectionColumn: isSelectable,
      selectable: rowIsSelectable(person),
      selected: selection?.isSelected(person.id) ?? false,
      selectionMode: selection?.selectionMode ?? false,
      onToggleSelect: selection?.toggle,
    };
  };

  const selectableVisibleIds = persons
    .filter((p) => rowIsSelectable(p))
    .map((p) => p.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("contacts:list.columns.name", { defaultValue: "Name" })}</Table.Th>
                <Table.Th style={{ width: 240 }}>
                  {t("contacts:list.columns.contact", { defaultValue: "Email / phone" })}
                </Table.Th>
                <Table.Th style={{ width: 200 }}>
                  {t("contacts:list.columns.tags", { defaultValue: "Tags" })}
                </Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("contacts:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <ContactTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : persons.map((person) => <ContactTableRow key={person.id} {...rowProps(person)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <ContactCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {persons.map((person) => (
            <motion.div
              key={person.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <ContactCard
                {...rowProps(person)}
                forceClose={shouldForceClose(person.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(person.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(person.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const ContactsList = React.memo(ContactsListComponent);
